import { useRef, useCallback, useEffect } from 'react';

interface UseEditorOptions {
  value: string;
  onChange: (value: string) => void;
}

/**
 * Hook for managing contentEditable editor state and formatting.
 * Handles value sync, formatting commands, and selection management.
 *
 * NOTE: Uses deprecated document.execCommand API. Still supported by all browsers
 * but should be migrated to the InputEvent API or a dedicated editor library
 * when this component moves to core-components.
 */
export const useEditor = ({ value, onChange }: UseEditorOptions) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalChange = useRef(false);
  const savedSelection = useRef<Range | null>(null);

  // Sync external value changes to the editor
  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value;
      }
    }
    isInternalChange.current = false;
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  // Find the link element if selection is within a link
  const findLinkElement = useCallback((): HTMLAnchorElement | null => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    let node: Node | null = selection.anchorNode;
    while (node && node !== editorRef.current) {
      if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'A') {
        return node as HTMLAnchorElement;
      }
      node = node.parentNode;
    }
    return null;
  }, []);

  // Apply formatting using execCommand
  const applyFormat = useCallback((command: string, commandValue?: string) => {
    const selection = window.getSelection();
    let savedRange: Range | null = null;
    if (selection && selection.rangeCount > 0) {
      savedRange = selection.getRangeAt(0).cloneRange();
    }

    editorRef.current?.focus();

    if (savedRange) {
      const newSelection = window.getSelection();
      if (newSelection) {
        newSelection.removeAllRanges();
        newSelection.addRange(savedRange);
      }
    }

    document.execCommand(command, false, commandValue);
    handleInput();
  }, [handleInput]);

  // Save current selection
  const saveSelection = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      savedSelection.current = selection.getRangeAt(0).cloneRange();
      return selection.toString();
    }
    return '';
  }, []);

  // Restore saved selection
  const restoreSelection = useCallback(() => {
    if (savedSelection.current) {
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(savedSelection.current);
      }
    }
  }, []);

  // Insert link HTML
  const insertLink = useCallback((text: string, url: string) => {
    let finalUrl = url.trim();
    if (!(/^https?:\/\//i).test(finalUrl)) {
      finalUrl = `https://${finalUrl}`;
    }

    // Escape HTML entities to prevent XSS from user-provided text
    const escapeHtml = (str: string) =>
      str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    editorRef.current?.focus();

    setTimeout(() => {
      restoreSelection();

      const existingLink = findLinkElement();
      if (existingLink && text === existingLink.textContent) {
        document.execCommand('createLink', false, finalUrl);
        const createdLink = findLinkElement();
        if (createdLink) {
          createdLink.setAttribute('target', '_blank');
        }
      } else {
        document.execCommand('delete');
        document.execCommand('insertHTML', false, `<a href="${escapeHtml(finalUrl)}" target="_blank">${escapeHtml(text)}</a>`);
      }

      handleInput();
      savedSelection.current = null;
    }, 10);
  }, [findLinkElement, handleInput, restoreSelection]);

  // Insert HTML at the current cursor position
  const insertHTML = useCallback((html: string) => {
    document.execCommand('insertHTML', false, html);
    handleInput();
  }, [handleInput]);

  // Insert plain text at the current cursor position
  const insertText = useCallback((text: string) => {
    document.execCommand('insertText', false, text);
  }, []);

  return {
    editorRef,
    handleInput,
    findLinkElement,
    applyFormat,
    saveSelection,
    restoreSelection,
    insertLink,
    insertHTML,
    insertText,
  };
};
